import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  CircularProgress,
  IconButton,
  Paper,
  Theme,
  Tooltip,
} from '@mui/material';
import { green } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { GridRowData, GridSortModel } from '@mui/x-data-grid';
import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation } from 'react-query';
import { Link as NavLink, Route, Switch } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';

import { useBusinessPermissions } from '../../common/helperFunctions/useBusinessPermissions';
import { trackAddIVR } from '../../common/userTracking';
import { isUrl } from '../../common/validators';
import { IUserAtom, user } from '../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../services/rest/getApiMutation';
import { endpointEnum } from '../../services/rest/makeAPIRequest';
import { DeleteIvrModal } from '../components/modals/DeleteIvrModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
} from '../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { IvrSettings } from '../subpages/IvrSettings';

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  actionColumn: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
  editIconButton: {
    color: theme.palette.primary.main,
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  audio: {
    transition: 'all 0.5s linear',
    boxShadow: '0px 0px 0px 0px',
    backgroundColor: '#f0f3f4',
    borderRadius: '7px 7px 7px 7px',
    height: 32,
    margin: '10px 0px',
    '&:focus': {
      outline: 0,
    },
  },
  // button: {
  //   marginTop: theme.spacing(0.5),
  //   marginRight: theme.spacing(1),
  // },
  disabled:
    theme.palette.mode === 'light'
      ? {
          backgroundColor: 'lightgrey',
          '&:hover': {
            backgroundColor: 'grey',
          },
        }
      : {
          backgroundColor: 'grey',
          '&:hover': {
            backgroundColor: 'darkgrey',
          },
        },
  messageColumn: {
    width: '100% !important' as '100%',
    minWidth: '400px !important' as '400%',
    lineHeight: 'normal !important' as 'normal',
    whiteSpace: 'normal !important' as 'normal',
    padding: theme.spacing(1) + 'px !important',
  },
}));

// specify sorting here

export function ManageIvrs({ history }) {
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  const classes = useStyles();
  // const setIvrIdAtom = useSetRecoilState(viewIvrId);
  // const [ivrId, setIvrId] = useState('');
  // const [storage] = useRecoilState(user);
  // console.log(storage, 'user atom');
  // state for opening/closing delete ivr modal
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  // state used to pass to other components
  const [selected, setSelected] = useState<GridRowData[]>([]);
  const [deleteGroupId, setDeleteGroupId] = useState<string | undefined>('');
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const userAtomContents = userAtom.contents as IUserAtom;
  const AddIvrLoadable = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.SUBMIT_NEW_IVR,
      waitForPayload: true,
    })
  );
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));
  const deleteIvrLoadable = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.DELETE_IVR,
      waitForPayload: true,
    })
  );
  const setRefetchToggleForBusinessIvrs = useSetRecoilState(
    refetchToggle(methodNameEnum.GET_BUSINESS_IVRS)
  );
  // function editAction(ivrId) {
  //   setIvrId(ivrId);
  // }
  const getBusinessIvrs = useRecoilValueLoadable(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_IVRS })
  );
  useEffect(() => {
    if (AddIvrLoadable.contents?.data) {
      setRefetchToggleForBusinessIvrs({});
    }
  }, [deleteIvrLoadable, AddIvrLoadable]);
  function formatter(params) {
    return deleteGroupId === params.row.mainIVRid ? (
      <div>
        <article className={clsx(classes.actionColumn, 'row')}>
          <Tooltip title="Confirm Deletion">
            <IconButton
              aria-label="accept"
              onClick={async (event) => {
                event.preventDefault(); // stop link
                // trigger delete and refetch -- TODO
                // setPayloadForDeleteIvr({
                // ivrid: params.row.mainIVRid,
                // });
                const data = {
                  call: methodNameEnum.DELETE_IVR,
                  apikey: userAtomContents.apikey,
                  businessid: userAtomContents.businessid,
                  ivrid: params.row.mainIVRid,
                };
                const response = await mutation.mutateAsync({
                  endpoint: endpointEnum.DELETE_IVR,
                  payload: data,
                });
                // enqueueSnackbarWithStyles({
                //   message: 'Successfully Deleted IVR',
                //   alertProps: { severity: 'success' },
                // });
                setRefetchToggleForBusinessIvrs({});
                if (response?.data?.success) {
                  enqueueSnackbarWithStyles({
                    message: `Successfully Deleted ${params.row.IVRname} IVR`,
                    alertProps: { severity: 'success' },
                  });
                } else {
                  enqueueSnackbarWithStyles({
                    message: `Encountered an error deleting the ${params.row.IVRname} IVR`,
                    alertProps: { severity: 'warning' },
                  });
                }
              }}
              disabled={deleteIvrLoadable.state === 'loading'}
              size="large"
            >
              <CheckIcon color="primary" />
              {deleteIvrLoadable.state === 'loading' && (
                <CircularProgress
                  size={43}
                  className={classes.buttonProgress}
                />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton
              aria-label="cancel"
              onClick={(event) => {
                event.preventDefault(); // stop link
                // reset groupid hide confirm / cancel deletion
                setDeleteGroupId(undefined);
              }}
              disabled={getBusinessIvrs.state === 'loading'}
              size="large"
            >
              <ClearIcon
                color="disabled"
                className={clsx({
                  [classes.deleteIconButton]:
                    getBusinessIvrs.state !== 'loading', // || deleteCancelTokenSource,
                })}
              />
            </IconButton>
          </Tooltip>
        </article>
      </div>
    ) : (
      <div className={clsx(classes.actionColumn, 'row')}>
        <NavLink to={`/manage/ivrs/${params.value}/edit`}>
          <Tooltip title="Edit">
            <IconButton name="edit" size="large">
              <CreateIcon color="primary" />
            </IconButton>
          </Tooltip>
        </NavLink>
        <Tooltip title="Delete">
          <IconButton
            aria-label="delete"
            onClick={() => {
              setDeleteGroupId(params.row.mainIVRid);
            }}
            size="large"
          >
            <DeleteIcon className={classes.deleteIconButton} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }
  function nameFormatter(params) {
    return (
      <Tooltip title="Edit">
        <NavLink to={`/manage/ivrs/${params.row.mainIVRid}/edit`}>
          {params.value}
        </NavLink>
      </Tooltip>
    );
  }
  const readOnly = useBusinessPermissions().accountReadOnly;
  const columns: IColDef[] = [
    {
      field: 'IVRname',
      headerName: 'Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: nameFormatter,
    },
    {
      field: 'type',
      headerName: 'Primary',
      width: 150,
      valueFormatter: (params) => (params.value === 'primary' ? 'Yes' : 'No'),
    },
    {
      field: 'message',
      headerName: 'Message',
      headerClassName: classes.messageColumn,
      cellClassName: classes.messageColumn,
      renderCell: (params) =>
        isUrl(params.value as string) ? (
          <audio
            controls
            src={params.value as string}
            className={classes.audio}
          >
            Your browser does not support the audio element.
          </audio>
        ) : (
          <>{params.value}</>
        ),
    },
    {
      field: 'hasDefaultAction',
      headerName: 'Has Default Action',
      width: 150,
    },
    {
      field: 'numberOfTiers',
      headerName: 'Options',
      width: 150,
    },
    {
      field: 'mainIVRid',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      hide: readOnly,
      width: 150,
      disableExport: true,
      enableSorting: false,
      disableSearch: true,
      renderCell: formatter,
    },
  ];

  const ivrsCached = useRef([]);
  ivrsCached.current = useMemo(
    () =>
      getBusinessIvrs?.contents?.data?.result
        ? getBusinessIvrs?.contents?.data?.result?.map((o) => ({
            ...o,
            id: o.mainIVRid,
          }))
        : ivrsCached.current,
    [getBusinessIvrs]
  );
  /**
   * Create cutom toolbar component for table
   * Use useCallback to stop rerendering
   */
  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        dataType="IVRs"
        numSelected={selected.length}
        rightComponent={
          <ToolbarAddDeleteButton
            numSelected={selected.length}
            onClickForAdd={() => {
              trackAddIVR();
              history.push('/manage/ivrs/add');
            }}
            onClickForDelete={() => {
              handleShow();
            }}
            isLoading={props.isLoading}
          />
        }
      />
    ),
    [handleShow, selected.length, readOnly]
  );
  const [sortModel, setGridSortModel] = React.useState<GridSortModel>([
    { field: 'IVRname', sort: 'asc' },
  ]);
  return (
    <Paper>
      <Switch>
        <Route path="/manage/ivrs/:id/edit">
          {({ history, match }) => (
            <IvrSettings
              show={match != null}
              id={match?.params.id}
              onHide={() => {
                history.push('/manage/ivrs');
              }}
            />
          )}
        </Route>
        <Route path="/manage/ivrs/add">
          {({ history, match }) => (
            <IvrSettings
              show={match != null}
              onHide={() => {
                history.push('/manage/ivrs');
              }}
              id={undefined}
            />
          )}
        </Route>
      </Switch>
      <div>
        <DataTable
          enableSelectAll={true}
          setSelected={setSelected}
          rows={ivrsCached?.current ?? []}
          columns={columns}
          loading={getBusinessIvrs.state !== 'hasValue'}
          ToolbarComponent={ToolbarComponent}
          sortModel={sortModel}
          setGridSortModel={setGridSortModel}
          selected={selected}
        />
      </div>
      <DeleteIvrModal
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        userAtom={userAtom}
        selected={selected}
        handleClose={handleClose}
        show={show}
        mutation={mutation}
        setRefetchToggleForBusinessIvrs={setRefetchToggleForBusinessIvrs}
        // handleDelete={deleteIvr}
      />
    </Paper>
  );
}
